import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import * as client from "../../../client";
import { ROUTES } from "../../../routes";
import { formatAbbreviatedMoney } from "../Money";
import { EditableRow, ReadOnlyRow, RowBadge, Section } from "./FieldRow";
import { formatCurrency, readableLayer, statusLabel } from "./util";
import styles from "./OverviewPanes.module.css";

type FieldValue = client.ResolvedField["value"];

// Display order for the policy-side sections, mirroring the backend registry
// (SECTION_ORDER). Sections owned by the submission pane are listed in
// SUBMISSION_SECTIONS and never render here; anything else the registry adds
// shows up on this side automatically.
const SECTION_ORDER = [
  "Quote Letter",
  "Premiums",
  "Terms",
  "Subjectivities & Comments",
];

const SUBMISSION_SECTIONS = new Set([
  "Named Insured",
  "Mailing Info",
  "Policy Dates",
  "Forms",
  "Perils",
  "Insuring Companies",
  "Schedule",
  "Limits",
  "Sublimits",
  "Time Limits",
  "Distance Limits",
  "Waiting Periods",
  "Deductibles",
]);

// Premium keys the layer stat tiles already show.
const LAYER_PREMIUM_KEYS = new Set([
  "premium:limit_of_liability",
  "premium:layer_range",
  "premium:participation",
]);

const STATUS_OPTIONS = Object.values(client.PolicyApplicationStatus).map(
  (status) => ({ label: statusLabel(status), value: status })
);

const sectionIndex = (section: string): number => {
  const i = SECTION_ORDER.indexOf(section);
  return i < 0 ? SECTION_ORDER.length : i;
};

const isBlank = (v: FieldValue): boolean =>
  v === null ||
  v === "" ||
  v === undefined ||
  (typeof v === "number" && Number.isNaN(v));

const has = (obj: Record<string, FieldValue>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

const layerChip = (layer: client.Layer): string => {
  const range = formatAbbreviatedMoney(layer.range);
  return layer.starting_point > 0
    ? `${range} xs ${formatAbbreviatedMoney(layer.starting_point)}`
    : `${range} primary`;
};

export type OverlayDraft = {
  draft: client.PolicyFieldOverlay;
  setUpsert: (key: string, value: FieldValue) => void;
  revert: (key: string) => void;
};

export type DetailsDraft = {
  status: client.PolicyApplicationStatus;
  setStatus: (value: client.PolicyApplicationStatus) => void;
};

interface PolicyPaneProps {
  application: client.PolicyApplicationView;
  /** The report's policy number; each policy is it plus the suffix. */
  reportPolicyNumber: string | null;
  siblings: client.PolicyApplicationView[];
  layersById: Map<string, client.Layer>;
  fields: client.ResolvedField[];
  overlay: OverlayDraft;
  details: DetailsDraft;
}

/**
 * The policy-owned half of the overview: identifiers, the layer's terms and
 * premium (read-only, they come from the Layers tab and pricing), and the
 * quote-letter terms this policy may override.
 */
export const PolicyPane: React.FC<PolicyPaneProps> = ({
  application,
  reportPolicyNumber,
  siblings,
  layersById,
  fields,
  overlay,
  details,
}) => {
  const navigate = useNavigate();
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const editing = (key: string) => editingKeys.has(key);
  const toggleEdit = (key: string) =>
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const layer = layersById.get(application.layer_id);
  const participation = layer
    ? (layer.participation ?? layer.range)
    : undefined;
  const sharePct =
    layer && participation !== undefined && layer.range > 0
      ? Math.round((participation / layer.range) * 1000) / 10
      : null;

  const premiumLines = fields.filter(
    (field) =>
      field.section === "Premiums" && !LAYER_PREMIUM_KEYS.has(field.key)
  );

  const draftValue = (field: client.ResolvedField): FieldValue =>
    has(overlay.draft.upserts, field.key)
      ? (overlay.draft.upserts[field.key] ?? null)
      : field.value;

  const displayValue = (field: client.ResolvedField, value: FieldValue) => {
    if (isBlank(value)) return "";
    if (field.input_type === "boolean") return value ? "Yes" : "No";
    return typeof value === "number" && field.section === "Premiums"
      ? formatCurrency(value)
      : String(value);
  };

  const renderEditor = (field: client.ResolvedField, value: FieldValue) => {
    switch (field.input_type) {
      case "select":
        return (
          <Dropdown
            value={value}
            options={(field.options ?? []).map((o) => ({ label: o, value: o }))}
            placeholder="Select…"
            onChange={(e) => overlay.setUpsert(field.key, e.value)}
          />
        );
      case "number":
        return (
          <InputNumber
            autoFocus
            value={typeof value === "number" ? value : null}
            useGrouping={false}
            maxFractionDigits={4}
            onValueChange={(e) => overlay.setUpsert(field.key, e.value ?? null)}
          />
        );
      default:
        return (
          <InputTextarea
            autoFocus
            autoResize
            rows={1}
            className={styles.input}
            value={value === null ? "" : String(value)}
            onChange={(e) => overlay.setUpsert(field.key, e.target.value)}
          />
        );
    }
  };

  const renderField = (field: client.ResolvedField) => {
    const value = draftValue(field);
    if (!field.editable) {
      return (
        <ReadOnlyRow
          key={field.key}
          label={field.label}
          value={displayValue(field, value)}
        />
      );
    }
    const overridden = has(overlay.draft.upserts, field.key);
    const invalid = field.required && isBlank(value);
    return (
      <EditableRow
        key={field.key}
        label={
          <>
            {field.label}
            {field.required && (
              <span style={{ color: "var(--red-500)" }}>*</span>
            )}
          </>
        }
        display={invalid ? "Required" : displayValue(field, value)}
        editing={editing(field.key)}
        onToggle={() => toggleEdit(field.key)}
        editor={renderEditor(field, value)}
        badge={
          overridden ? (
            <RowBadge tone="override">Overridden</RowBadge>
          ) : undefined
        }
        extra={
          overridden ? (
            <Button
              icon="pi pi-replay"
              rounded
              text
              size="small"
              type="button"
              tooltip="Revert to the submission default"
              tooltipOptions={{ showDelay: 400 }}
              onClick={() => overlay.revert(field.key)}
            />
          ) : undefined
        }
      />
    );
  };

  const sections = [
    ...new Set(
      fields
        .map((field) => field.section)
        .filter((section) => !SUBMISSION_SECTIONS.has(section))
        .filter((section) => section !== "Premiums")
    ),
  ].sort((a, b) => sectionIndex(a) - sectionIndex(b));

  // Two stacked sums, as on the letter: the per-peril premiums total to the
  // layer premium; our share plus TRIA and the fee total to the premium due.
  const renderStatementLine = (field: client.ResolvedField) => {
    const isSubtotal = field.key === "premium:total";
    const isGrandTotal = field.key === "premium:total_due";
    return (
      <React.Fragment key={field.key}>
        {field.key === "premium:annual" && (
          <div className={styles.stmtDivider} />
        )}
        <div
          className={`${styles.stmtRow} ${
            isSubtotal || isGrandTotal ? styles.stmtTotal : ""
          } ${isGrandTotal ? styles.stmtAccent : ""}`}
        >
          <span>{field.label}</span>
          <span className={styles.money}>
            {displayValue(field, field.value) || "—"}
          </span>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.pane}>
      <header className={styles.paneHeader}>
        <div className={styles.paneTitleRow}>
          <h2 className={styles.paneTitle}>Policy {application.suffix}</h2>
        </div>
        <p className={styles.paneSub}>
          {layer ? readableLayer(layer) : "Layer not found on the report"}.
          Edits here apply to this policy only.
        </p>
        {siblings.length > 1 && (
          <nav className={styles.switcher}>
            {siblings.map((sibling) => {
              const siblingLayer = layersById.get(sibling.layer_id);
              const active = sibling.id === application.id;
              return (
                <button
                  key={sibling.id}
                  type="button"
                  className={`${styles.switchTab} ${
                    active ? styles.switchTabActive : ""
                  }`}
                  onClick={() =>
                    !active &&
                    navigate(ROUTES.DASHBOARD_POLICY_APPLICATION(sibling.id))
                  }
                >
                  <span className={styles.switchSuffix}>{sibling.suffix}</span>
                  {siblingLayer && (
                    <span className={styles.switchLayer}>
                      {layerChip(siblingLayer)}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        )}
      </header>

      <Section title="Policy">
        <ReadOnlyRow
          label="Policy number"
          value={
            reportPolicyNumber
              ? `${reportPolicyNumber}-${application.suffix}`
              : null
          }
          note={reportPolicyNumber ? undefined : "Assigned at binding"}
        />
        <EditableRow
          label="Status"
          display={statusLabel(details.status)}
          editing={editing("status")}
          onToggle={() => toggleEdit("status")}
          editor={
            <Dropdown
              options={STATUS_OPTIONS}
              value={details.status}
              onChange={(e) => details.setStatus(e.value)}
            />
          }
        />
      </Section>

      <Section title="Layer & premium" hint="Edited on the Layers tab">
        {layer && (
          <div className={styles.stats}>
            <div className={styles.stat}>
              <div className={styles.statLabel}>Limit</div>
              <div className={styles.statValue}>
                {formatAbbreviatedMoney(layer.range)}
              </div>
              <div className={styles.statSub}>
                {formatCurrency(layer.range)}
              </div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statLabel}>Attachment</div>
              <div className={styles.statValue}>
                {layer.starting_point > 0
                  ? formatAbbreviatedMoney(layer.starting_point)
                  : "Primary"}
              </div>
              <div className={styles.statSub}>
                {layer.starting_point > 0
                  ? `xs ${formatCurrency(layer.starting_point)}`
                  : "Ground up"}
              </div>
            </div>
            <div className={styles.stat}>
              <div className={styles.statLabel}>Our share</div>
              <div className={styles.statValue}>
                {sharePct === null ? "—" : `${sharePct}%`}
              </div>
              <div className={styles.statSub}>
                {participation !== undefined
                  ? `${formatCurrency(participation)} part of ${formatAbbreviatedMoney(layer.range)}`
                  : ""}
              </div>
            </div>
          </div>
        )}
        {premiumLines.length > 0 ? (
          <div className={styles.statement}>
            {premiumLines.map(renderStatementLine)}
          </div>
        ) : (
          <div className={styles.stmtEmpty}>
            Pricing isn&apos;t available for this layer yet.
          </div>
        )}
      </Section>

      {sections.map((section) => (
        <Section key={section} title={section}>
          {fields.filter((field) => field.section === section).map(renderField)}
        </Section>
      ))}
    </div>
  );
};
